import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import toast from "react-hot-toast";
import { AlertTriangle, Link2Off } from "lucide-react";

import PlayerCard from "./PlayerCard";
import { EmptyState } from "../agency/v2/states";
import { clubService } from "../../services/api";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { DETAILED_POSITION_OPTIONS } from "../common/DetailedPositionsControl";
import DetailedPositionsControl from "../common/DetailedPositionsControl";
import PositionFilterDropdown from "../common/PositionFilterDropdown";
import { useNavigate } from "react-router-dom";
import NationalityFlags from "../common/NationalityFlags";

const MyPlayers = forwardRef(
  (
    {
      isQuickEdit,
      setIsQuickEdit,
      onRegisterSave,
      onSavingStateChange,
      canEditSquad,
    },
    ref
  ) => {
    const [selectedPosition, setSelectedPosition] = useState("all");
    const [playersForSale, setPlayersForSale] = useState({});
    const [playersForLoan, setPlayersForLoan] = useState({});
    const [playerStatusChanges, setPlayerStatusChanges] = useState({}); // Track status changes
    const [players, setPlayers] = useState([]);
    // const [clubData, setClubData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [isSaving, setIsSaving] = useState(false); // Track saving state
    const [viewMode, setViewMode] = useState(() => {
      // Initialize from localStorage, default to 'roster' if not found
      const savedViewMode = localStorage.getItem("myPlayersViewMode");
      return savedViewMode || "roster";
    }); // 'roster' or 'list'
    const saveHandlerRef = useRef(null);
    const navigate = useNavigate();

    // Create positions array with all detailed positions
    const positions = [
      { id: "all", label: "All positions" },
      { id: "goalkeeper", label: "Goalkeeper" },
      { id: "defender", label: "Defender" },
      { id: "midfielder", label: "Midfielder" },
      { id: "forward", label: "Forward" },
      ...DETAILED_POSITION_OPTIONS.map((option) => ({
        id: option.value,
        label: option.value.toUpperCase(),
        fullLabel: option.label,
      })),
      { id: "unknown", label: "Position not set" },
    ];

    // Fetch club squad data
    const fetchClubSquad = useCallback(async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await clubService.getMyClubSquad();
        if (response.success) {
          // setClubData(response.data.club);
          console.log("response data of my players", response);
          setPlayers(response.data || []);
        } else {
          setError(response.error);
        }
      } catch (error) {
        setError("Failed to load squad data");
      } finally {
        setIsLoading(false);
      }
    }, []);

    // Load data on component mount
    useEffect(() => {
      fetchClubSquad();
    }, [fetchClubSquad]);

    // Save view mode to localStorage whenever it changes
    useEffect(() => {
      localStorage.setItem("myPlayersViewMode", viewMode);
    }, [viewMode]);

    // Update ref whenever handler dependencies change
    useEffect(() => {
      saveHandlerRef.current = handleSaveChanges;
    });

    // Register save handler with parent
    useEffect(() => {
      if (onRegisterSave) {
        // Register a function that calls the ref
        onRegisterSave(() => saveHandlerRef.current);
      }
    }, [onRegisterSave]);

    // Helper function to get country code from country name or country_image_path
    const getCountryCode = (countryName, countryImagePath) => {
      if (countryImagePath) {
        // Extract country code from image path like "https://flagcdn.com/w40/es.png" or "https://cdn.sportmonks.com/images/countries/png/short/es.png"
        const match = countryImagePath.match(/\/([a-z]{2})\.png$/);
        if (match) return match[1];
      }

      if (!countryName) return "xx";
      // Comprehensive mapping
      const countryMap = {
        England: "en",
        Spain: "es",
        Germany: "de",
        Portugal: "pt",
        Egypt: "eg",
        Brazil: "br",
        France: "fr",
        Italy: "it",
        Argentina: "ar",
        Netherlands: "nl",
        Croatia: "hr",
        Angola: "ao",
        Turkey: "tr",
        Uruguay: "uy",
        Austria: "at",
        Belgium: "be",
        Ukraine: "ua",
      };
      return countryMap[countryName] || "xx";
    };

    function calculateExpiry(expiryDateStr) {
      if (!expiryDateStr || expiryDateStr === "N/A") return "-";

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

      const expiryDate = new Date(expiryDateStr);
      expiryDate.setHours(0, 0, 0, 0); // Reset time to start of day

      if (isNaN(expiryDate.getTime())) return "-";

      // Check if the contract has already expired
      if (expiryDate < today) {
        return "Expired";
      }

      let years = expiryDate.getFullYear() - today.getFullYear();
      let months = expiryDate.getMonth() - today.getMonth();
      let days = expiryDate.getDate() - today.getDate();

      // Adjust days/months if negative
      if (days < 0) {
        months -= 1;
        const prevMonth = new Date(
          expiryDate.getFullYear(),
          expiryDate.getMonth(),
          0
        );
        days += prevMonth.getDate();
      }
      if (months < 0) {
        years -= 1;
        months += 12;
      }

      let result = [];
      if (years > 0) result.push(`${years} year${years > 1 ? "s" : ""}`);
      if (months > 0) result.push(`${months} month${months > 1 ? "s" : ""}`);
      if (days > 0 && years === 0)
        result.push(`${days} day${days > 1 ? "s" : ""}`);

      return result.length > 0 ? result.join(" ") : "Expired";
    }

    // Transform API data to match component expected format
    const transformPlayerData = (apiPlayer) => {
      const tpDetailedPosition =
        apiPlayer.tp_detailed_position ||
        apiPlayer.tpDetailedPosition ||
        apiPlayer.tpDetailedPositions ||
        null;

      const playerAccountId =
        apiPlayer.player_id ||
        apiPlayer.account_id ||
        apiPlayer.agent_player_id ||
        apiPlayer.ws_player_id ||
        apiPlayer.id;

      const wyscoutPlayerId =
        apiPlayer.wyscout_player_id ||
        apiPlayer.wyscout_id ||
        apiPlayer.ws_player_id ||
        apiPlayer.wyscout_player?.id ||
        apiPlayer.id; // fallback to id when it's the Wyscout ID

      const countryName =
        apiPlayer.country?.name || apiPlayer.country || "Unknown";
      const countryCodeAlpha2 = apiPlayer.country?.alpha2code
        ? String(apiPlayer.country.alpha2code).toLowerCase()
        : null;
      const resolvedCountryCode =
        countryCodeAlpha2 ||
        getCountryCode(
          countryName,
          apiPlayer.country?.image_path || apiPlayer.country_image_path
        );

      return {
        id: apiPlayer.id,
        name: apiPlayer.name,
        position: apiPlayer.position || "Position not set",
        country: countryName,
        countryCode: resolvedCountryCode,
        gamesPlayed:
          typeof apiPlayer.season_stats?.appearances === "number"
            ? apiPlayer.season_stats.appearances
            : "-",
        contractExpiry: calculateExpiry(apiPlayer?.contract_expires_on),
        age: apiPlayer.age,
        image:
          apiPlayer.image_url ||
          apiPlayer.platform_data?.player_image ||
          apiPlayer.image_path,
        forSale: apiPlayer.is_available_for_sale,
        forLoan: apiPlayer.is_available_for_loan,
        transferFee: apiPlayer.transfer_fee
          ? parseFloat(apiPlayer.transfer_fee)
          : null,
        loanFee: apiPlayer.loan_fee ? parseFloat(apiPlayer.loan_fee) : null,
        height: apiPlayer.height,
        weight: apiPlayer.weight,
        transfermarktId: apiPlayer.transfermarkt_id,
        isTransferPitchUser: apiPlayer.is_transferpitch_user,
        jerseyNumber:
          apiPlayer.shirt_number !== undefined
            ? apiPlayer.shirt_number
            : apiPlayer.jersey_number,
        tpDetailedPosition,
        tp_detailed_position: tpDetailedPosition,
        playerAccountId,
        wyscoutPlayerId,
        original: apiPlayer,
        contract_expires_on: apiPlayer.contract_expires_on,
        birth_country: apiPlayer?.birth_country,
        nationality: apiPlayer?.nationality,
      };
    };

    const handleSaveChanges = async () => {
      // Build array of player updates based on current selections
      const playerUpdates = [];

      // Process all changes
      const allChanges = new Set([
        ...Object.keys(playersForSale),
        ...Object.keys(playersForLoan),
        ...Object.keys(playerStatusChanges),
      ]);

      allChanges.forEach((playerId) => {
        const statusChange = playerStatusChanges[playerId];
        const saleAmount = playersForSale[playerId];
        const loanAmount = playersForLoan[playerId];

        // Determine the final status
        let status = "not_available";
        const updateObj = {
          player_id: parseInt(playerId),
        };

        if (statusChange) {
          status = statusChange;
        } else if (saleAmount) {
          status = "available_for_transfer";
          updateObj.transfer_fee = parseFloat(saleAmount);
        } else if (loanAmount) {
          status = "available_for_loan";
          updateObj.loan_fee = parseFloat(loanAmount);
        }

        updateObj.status = status;
        playerUpdates.push(updateObj);
      });

      if (playerUpdates.length === 0) {
        setIsQuickEdit(false);
        return;
      }

      try {
        setIsSaving(true);
        if (onSavingStateChange) onSavingStateChange(true);

        const response = await clubService.updatePlayerAvailability(
          playerUpdates
        );
        if (response.success) {
          // Refresh squad data to reflect changes
          await fetchClubSquad();
          setIsQuickEdit(false);
          setPlayersForSale({});
          setPlayersForLoan({});
          setPlayerStatusChanges({});
          toast.success("Player availability updated successfully");
        } else {
          toast.error(response.error || "Failed to update player availability");
          console.error(
            "Failed to update player availability:",
            response.error
          );
        }
      } catch (error) {
        toast.error("Error updating player availability");
        console.error("Error updating player availability:", error);
      } finally {
        setIsSaving(false);
        if (onSavingStateChange) onSavingStateChange(false);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSaveChanges: handleSaveChanges,
    }));

    const handleDetailedPositionsSave = useCallback(
      async (playerCardData, { formattedPositions }) => {
        const cleanedPositions = { ...formattedPositions };
        Object.keys(cleanedPositions).forEach((key) => {
          if (!cleanedPositions[key]) {
            delete cleanedPositions[key];
          }
        });

        const playerId =
          playerCardData.playerAccountId ||
          playerCardData.original?.player_id ||
          playerCardData.original?.account_id ||
          playerCardData.id;

        const wyscoutPlayerId =
          playerCardData.wyscoutPlayerId ||
          playerCardData.original?.wyscout_player_id ||
          playerCardData.original?.wyscout_id ||
          playerCardData.original?.ws_player_id;

        if (!playerId && !wyscoutPlayerId) {
          toast.error(
            `Couldn't identify ${
              playerCardData.name || "player"
            } for updating positions.`
          );
          throw new Error(
            "Missing player identifier for detailed positions update"
          );
        }

        const payloadItem = {
          tp_detailed_position: cleanedPositions,
        };

        if (playerId) {
          payloadItem.player_id = playerId;
        }
        if (wyscoutPlayerId) {
          payloadItem.wyscout_player_id = wyscoutPlayerId;
        }

        const response = await clubService.updateDetailedPositions([
          payloadItem,
        ]);

        if (!response.success) {
          toast.error(response.error || "Failed to update detailed positions");
          throw new Error(
            response.error || "Failed to update detailed positions"
          );
        }

        if (response.errors && response.errors.length) {
          const firstError =
            typeof response.errors[0] === "string"
              ? response.errors[0]
              : response.errors[0]?.message;
          toast.error(firstError || "Failed to update detailed positions");
          throw new Error(firstError || "Failed to update detailed positions");
        }

        toast.success("Detailed positions updated");

        setPlayers((prev) =>
          prev.map((rawPlayer) => {
            const matches =
              rawPlayer.id === playerCardData.original?.id ||
              rawPlayer.player_id === playerId ||
              rawPlayer.account_id === playerId ||
              rawPlayer.wyscout_player_id === wyscoutPlayerId ||
              rawPlayer.ws_player_id === wyscoutPlayerId;

            if (!matches) return rawPlayer;

            return {
              ...rawPlayer,
              tp_detailed_position: cleanedPositions,
              tpDetailedPosition: cleanedPositions,
            };
          })
        );
      },
      [setPlayers]
    );

    const handleContractExpirySave = useCallback(
      async (playerCardData, { contractExpiryDate }) => {
        const playerId = playerCardData?.id;
        const wyscoutPlayerId =
          playerCardData?.wyscoutPlayerId ||
          playerCardData?.original?.wyscout_player_id ||
          playerCardData?.original?.wyscout_id ||
          playerCardData?.original?.ws_player_id;

        if (!playerId && !wyscoutPlayerId) {
          toast.error(
            `Couldn't identify ${
              playerCardData?.name || "player"
            } for updating contract.`
          );
          throw new Error("Missing player identifier for contract update");
        }

        // Format date to YYYY-MM-DD
        const formattedDate = new Date(contractExpiryDate)
          .toISOString()
          .split("T")[0];

        const payloadItem = {
          contract_expiration: formattedDate,
        };

        if (playerId) {
          payloadItem.player_id = playerId;
        }
        if (wyscoutPlayerId) {
          payloadItem.wyscout_player_id = wyscoutPlayerId;
        }

        const response = await clubService.updateClubPlayerContract([
          payloadItem,
        ]);

        if (!response.success) {
          toast.error(response.error || "Failed to update contract expiry");
          throw new Error(response.error || "Failed to update contract expiry");
        }

        if (response.errors && response.errors.length) {
          const firstError =
            typeof response.errors[0] === "string"
              ? response.errors[0]
              : response.errors[0]?.message;
          toast.error(firstError || "Failed to update contract expiry");
          throw new Error(firstError || "Failed to update contract expiry");
        }

        toast.success("Contract expiry updated successfully");

        // Update the local state with the new contract expiry
        setPlayers((prev) =>
          prev.map((rawPlayer) => {
            const matches =
              rawPlayer.id === playerCardData?.id ||
              rawPlayer.player_id === playerId ||
              rawPlayer.wyscout_player_id === wyscoutPlayerId ||
              rawPlayer.ws_player_id === wyscoutPlayerId;

            if (!matches) return rawPlayer;

            return {
              ...rawPlayer,
              contract_expires_on: formattedDate,
              contractExpiresOn: formattedDate,
            };
          })
        );
      },
      [setPlayers]
    );

    // Photo review flow: submits a new profile photo for a squad player. The
    // backend answers with status "pending" — no local state to update, the
    // photo only changes once the review approves it.
    const handlePlayerPhotoUpload = useCallback(async ({ player, file }) => {
      const wyscoutPlayerId =
        player?.wyscoutPlayerId ||
        player?.original?.wyscout_player_id ||
        player?.original?.wyscout_id ||
        player?.original?.ws_player_id;

      if (!wyscoutPlayerId) {
        toast.error(
          `Couldn't identify ${player?.name || "player"} for uploading a photo.`
        );
        throw new Error("Missing player identifier for photo upload");
      }

      const response = await clubService.uploadClubPlayerPhoto(
        wyscoutPlayerId,
        file
      );

      if (!response.success) {
        toast.error(
          response.error || "Failed to upload the photo. Please try again."
        );
        throw new Error(
          response.error || "Failed to upload the photo. Please try again."
        );
      }

      toast.success(
        "Photo submitted for review. It can take up to 24 hours until the new profile photo is visible."
      );
    }, []);

    const handleAddToSale = (playerId, fee) => {
      // If fee is empty (first click), use existing transfer fee if available
      if (fee === "") {
        const player = transformedPlayers.find((p) => p.id === playerId);
        fee = player?.transferFee ? player.transferFee.toString() : "";
      }
      setPlayersForSale((prev) => ({
        ...prev,
        [playerId]: fee,
      }));
      // Remove from loan if it was there
      setPlayersForLoan((prev) => {
        const newState = { ...prev };
        delete newState[playerId];
        return newState;
      });
    };

    const handleAddToLoan = (playerId, fee) => {
      // If fee is empty (first click), use existing loan fee if available
      if (fee === "") {
        const player = transformedPlayers.find((p) => p.id === playerId);
        fee = player?.loanFee ? player.loanFee.toString() : "";
      }
      setPlayersForLoan((prev) => ({
        ...prev,
        [playerId]: fee,
      }));
      // Remove from sale if it was there
      setPlayersForSale((prev) => {
        const newState = { ...prev };
        delete newState[playerId];
        return newState;
      });
    };

    const handleRemoveFromSale = (playerId) => {
      setPlayersForSale((prev) => {
        const newState = { ...prev };
        delete newState[playerId];
        return newState;
      });
    };

    const handleRemoveFromLoan = (playerId) => {
      setPlayersForLoan((prev) => {
        const newState = { ...prev };
        delete newState[playerId];
        return newState;
      });
    };

    const handleStatusChange = (playerId, status, fee) => {
      setPlayerStatusChanges((prev) => ({
        ...prev,
        [playerId]: status,
      }));

      // Clear the player from sale/loan states if marking as not available
      if (status === "not_available") {
        setPlayersForSale((prev) => {
          const newState = { ...prev };
          delete newState[playerId];
          return newState;
        });
        setPlayersForLoan((prev) => {
          const newState = { ...prev };
          delete newState[playerId];
          return newState;
        });
      }
    };

    // Handle player removal from squad
    const handlePlayerRemoved = (playerId) => {
      setPlayers((prevPlayers) =>
        prevPlayers.filter((p) => p.id !== playerId && p.playerId !== playerId)
      );

      // Clean up related states
      setPlayersForSale((prev) => {
        const newState = { ...prev };
        delete newState[playerId];
        return newState;
      });

      setPlayersForLoan((prev) => {
        const newState = { ...prev };
        delete newState[playerId];
        return newState;
      });

      setPlayerStatusChanges((prev) => {
        const newState = { ...prev };
        delete newState[playerId];
        return newState;
      });
    };

    // Handle player removal from squad with API call
    const handleRemoveFromSquad = async (player, reason) => {
      const playerId = player?.id || player?.playerId;
      const wyscoutPlayerId =
        player?.wyscoutPlayerId ||
        player?.original?.wyscout_player_id ||
        player?.original?.wyscout_id ||
        player?.original?.ws_player_id;

      if (!playerId && !wyscoutPlayerId) {
        toast.error("Unable to identify player");
        throw new Error("Unable to identify player");
      }

      const payloadItem = {
        removal_reason: reason,
      };

      if (playerId) {
        payloadItem.player_id = playerId;
      }
      if (wyscoutPlayerId) {
        payloadItem.wyscout_player_id = wyscoutPlayerId;
      }

      try {
        const response = await clubService.removePlayerFromSquad(payloadItem);

        if (!response.success) {
          toast.error(response.error || "Failed to remove player from squad");
          throw new Error(
            response.error || "Failed to remove player from squad"
          );
        }

        if (response.errors && response.errors.length) {
          const errorMsg = response.errors.map((e) => e.error).join(", ");
          toast.error(errorMsg);
          throw new Error(errorMsg);
        }

        toast.success(`${player.name} removed from squad`);
        handlePlayerRemoved(playerId);
      } catch (error) {
        console.error("Error removing player from squad:", error);
        throw error;
      }
    };

    // Get transformed players data
    const transformedPlayers = players.map(transformPlayerData);

    // Optimized filtering function
    const filterPlayersByPosition = useCallback((players, position) => {
      if (position === "all") return players;

      return players.filter((player) => {
        const playerPosition = player.position || "";

        // Handle the special case for unknown positions
        if (position === "unknown") {
          return (
            playerPosition === "Position not set" ||
            playerPosition === "" ||
            !playerPosition
          );
        }

        const selectedLower = position.toLowerCase();
        const positionLower = playerPosition.toLowerCase().trim();

        // Define general category to detailed position mapping
        const generalToDetailedMapping = {
          goalkeeper: [
            "g", "gk", "gkp",
          ],
          defender: [
            "d", "def", "dc", "cb", "lb", "rb", "lwb", "rwb",
            "dl", "dr", "wbl", "wbr", "wb", "sw",
            "lcb", "rcb",
            "rb5", "lb5", "rcb3", "lcb3",
          ],
          midfielder: [
            "m", "mid", "dm", "cdm", "cm", "mc", "am", "om", "cam",
            "lm", "rm", "ml", "mr",
            "dmc", "amc", "aml", "amr", "dml", "dmr",
            "lcm", "rcm", "ldm", "rdm", "lam", "ram",
            "rcmf", "lcmf", "rcmf3", "lcmf3",
            "dmf", "rdmf", "ldmf",
            "amf", "ramf", "lamf",
          ],
          forward: [
            "f", "fwd", "lw", "rw", "cf", "st", "ss", "df", "fw",
            "wf", "if", "lf", "rf", "ls", "rs",
            "rwf", "lwf",
          ],
          attacker: [
            "f", "fwd", "lw", "rw", "cf", "st", "ss", "df", "fw",
            "wf", "if", "lf", "rf", "ls", "rs",
            "rwf", "lwf",
          ],
        };

        // Check if player has detailed positions
        const playerDetailedPositions =
          player.tp_detailed_position || player.tpDetailedPosition || {};

        // If player has detailed positions, check both detailed codes and general categories
        if (
          playerDetailedPositions.primary_position ||
          playerDetailedPositions.secondary_position ||
          playerDetailedPositions.teritory_position ||
          playerDetailedPositions.tertiary_position
        ) {
          // Get player's detailed position codes (normalize to lowercase)
          const playerDetailedCodes = [
            playerDetailedPositions.primary_position,
            playerDetailedPositions.secondary_position,
            playerDetailedPositions.teritory_position,
            playerDetailedPositions.tertiary_position,
          ]
            .filter(Boolean)
            .map((code) => String(code).toLowerCase());

          // Check if selected position is a general category
          if (generalToDetailedMapping[selectedLower]) {
            // Selected position is a general category (goalkeeper, defender, midfielder, forward)
            const allowedDetailedCodes = generalToDetailedMapping[selectedLower];
            // Check if any of the player's detailed positions match the general category
            if (
              playerDetailedCodes.some((code) =>
                allowedDetailedCodes.includes(code)
              )
            ) {
              return true;
            }
            return false;
          }

          // Selected position is a specific detailed position code
          // Check if it matches any of the player's detailed positions
          const hasMatchingDetailedPosition = playerDetailedCodes.some(
            (code) => code === selectedLower
          );

          if (hasMatchingDetailedPosition) {
            return true;
          }

          // No match found for players with detailed positions
          return false;
        }

        // For players without detailed positions, use basic position matching

        // Direct match for general categories
        if (positionLower === selectedLower) {
          return true;
        }

        // Map general positions to their variations
        const generalPositionMappings = {
          goalkeeper: ["goalkeeper", "gk", "goalie"],
          defender: ["defender", "defence", "defense"],
          midfielder: ["midfielder", "midfield"],
          forward: ["forward", "attacker", "striker", "attack"],
          attacker: ["forward", "attacker", "striker", "attack"],
        };

        // Check if the player's position matches any variation of the selected general category
        const variations = generalPositionMappings[selectedLower] || [];
        if (
          variations.some(
            (term) => positionLower === term || positionLower.includes(term)
          )
        ) {
          return true;
        }

        // Check if selected position is a general category and player's position code matches
        if (generalToDetailedMapping[selectedLower]) {
          // Selected is a general category (goalkeeper, defender, midfielder, forward)
          // Check if player's position code is in that category
          const allowedCodes = generalToDetailedMapping[selectedLower];
          if (allowedCodes.includes(positionLower)) {
            return true;
          }
        }

        // Check if the player's general position includes the selected detailed code
        const detailedCodes = generalToDetailedMapping[positionLower] || [];
        if (detailedCodes.includes(selectedLower)) {
          return true;
        }

        // Also check exact position name mappings for specific cases
        const exactMappings = {
          gk: ["goalkeeper"],
          rb: ["right back", "right-back"],
          rcb: ["right centre back", "right center back"],
          lcb: ["left centre back", "left center back"],
          cb: ["centre back", "center back", "central defender"],
          lb: ["left back", "left-back"],
          rwb: ["right wing back", "right wingback"],
          lwb: ["left wing back", "left wingback"],
          cm: ["centre midfielder", "center midfielder", "central midfielder"],
          dmf: ["defensive midfielder"],
          amf: ["attacking midfielder"],
          rw: ["right winger", "right wing"],
          lw: ["left winger", "left wing"],
          cf: [
            "centre forward",
            "center forward",
            "striker",
            "central forward",
          ],
          ss: ["second striker", "support striker"],
        };

        const exactTerms = exactMappings[selectedLower] || [];
        return exactTerms.some((term) => positionLower === term);
      });
    }, []);

    // Filter players by position using the optimized function
    const filteredPlayers = useMemo(() => {
      return filterPlayersByPosition(transformedPlayers, selectedPosition);
    }, [transformedPlayers, selectedPosition, filterPlayersByPosition]);

    // Sort players by general position order: GK -> DEF -> MID -> FWD
    const sortedPlayers = useMemo(() => {
      const GENERAL_ORDER = ["goalkeeper", "defender", "midfielder", "forward"]; // attacker treated as forward

      const generalToDetailedMapping = {
        goalkeeper: [
          "g", "gk", "gkp",
        ],
        defender: [
          "d", "def", "dc", "cb", "lb", "rb", "lwb", "rwb",
          "dl", "dr", "wbl", "wbr", "wb", "sw",
          "lcb", "rcb",
          "rb5", "lb5", "rcb3", "lcb3",
        ],
        midfielder: [
          "m", "mid", "dm", "cdm", "cm", "mc", "am", "om", "cam",
          "lm", "rm", "ml", "mr",
          "dmc", "amc", "aml", "amr", "dml", "dmr",
          "lcm", "rcm", "ldm", "rdm", "lam", "ram",
          "rcmf", "lcmf", "rcmf3", "lcmf3",
          "dmf", "rdmf", "ldmf",
          "amf", "ramf", "lamf",
        ],
        forward: [
          "f", "fwd", "lw", "rw", "cf", "st", "ss", "df", "fw",
          "wf", "if", "lf", "rf", "ls", "rs",
          "rwf", "lwf",
        ],
        attacker: [
          "f", "fwd", "lw", "rw", "cf", "st", "ss", "df", "fw",
          "wf", "if", "lf", "rf", "ls", "rs",
          "rwf", "lwf",
        ],
      };

      const codeToGeneral = Object.entries(generalToDetailedMapping).reduce(
        (acc, [general, codes]) => {
          codes.forEach((code) => {
            acc[code] = general;
          });
          return acc;
        },
        {}
      );

      const getGeneralCategory = (player) => {
        const dp =
          player.tp_detailed_position || player.tpDetailedPosition || {};
        const candidateCodes = [
          dp.primary_position,
          dp.secondary_position,
          dp.teritory_position,
          dp.tertiary_position,
        ]
          .filter(Boolean)
          .map((c) => String(c).toLowerCase());

        for (const code of candidateCodes) {
          const general = codeToGeneral[code];
          if (general) return general;
        }

        const pos = (player.position || "").toLowerCase();
        if (pos.includes("goalkeeper")) return "goalkeeper";
        if (
          pos.includes("defender") ||
          pos.includes("back") ||
          pos.includes("wingback")
        )
          return "defender";
        if (pos.includes("midfielder") || pos.includes("midfield"))
          return "midfielder";
        if (
          pos.includes("forward") ||
          pos.includes("attacker") ||
          pos.includes("striker") ||
          pos.includes("winger")
        )
          return "forward";
        return "unknown";
      };

      const rank = (general) => {
        const idx = GENERAL_ORDER.indexOf(general);
        return idx === -1 ? GENERAL_ORDER.length : idx;
      };

      return [...filteredPlayers].sort((a, b) => {
        const ra = rank(getGeneralCategory(a));
        const rb = rank(getGeneralCategory(b));
        return ra - rb;
      });
    }, [filteredPlayers]);

    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mr-3"></div>
          <span className="text-gray-500">Loading squad...</span>
        </div>
      );
    }

    if (error) {
      // My Players is the landing surface of /club, so whatever this branch
      // renders is the first thing a club admin reads. The squad endpoint
      // answers 404 "Club or Wyscout team missing…" for a club whose record
      // carries no Wyscout team, and that sentence used to be printed raw.
      // Linking the team is a back-office job no club admin can do alone, so
      // that case explains who fixes it and points at the support button. The
      // marker word is all we have to recognise it: getMyClubSquad passes the
      // message through but drops the status code (services/api.js
      // getMyClubSquad). Every other failure keeps the wording it had.
      const isMissingClubLink = /wyscout/i.test(String(error));

      return (
        <div className="px-4 md:px-12 py-12 flex justify-center bg-[#F8F9FB]">
          <div className="w-full" style={{ maxWidth: 520 }}>
            <EmptyState
              icon={
                isMissingClubLink ? (
                  <Link2Off size={24} strokeWidth={1.8} color="#2283FF" />
                ) : (
                  <AlertTriangle size={24} strokeWidth={1.8} color="#2283FF" />
                )
              }
              title={
                isMissingClubLink
                  ? "Your squad is not linked yet"
                  : "Error loading squad"
              }
              body={
                isMissingClubLink
                  ? "TransferPitch builds this list from your club's official team data, and your club is not connected to it yet. Only we can make that connection, so message us with the support button and we will set it up."
                  : error
              }
              bodyMaxWidth={400}
              cta={
                <button
                  type="button"
                  onClick={fetchClubSquad}
                  className="inline-flex items-center justify-center rounded-lg bg-[#2283FF] px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-blue-600"
                >
                  Try again
                </button>
              }
            />
          </div>
        </div>
      );
    }

    return (
      <div className="px-4 mt-4 md:mt-0 md:px-12 bg-[#F8F9FB]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-4">
          <div className="flex items-center gap-3">
            <PositionFilterDropdown
              positions={positions}
              selectedId={selectedPosition}
              onSelect={setSelectedPosition}
              className="w-full md:w-auto"
            />

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-white border border-[#E6E6E6] rounded-lg p-1">
              <button
                onClick={() => setViewMode("roster")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "roster"
                    ? "bg-[#2283FF] text-white"
                    : "text-[#1E1E1E] hover:bg-gray-100"
                }`}
                title="Roster View"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="inline-block"
                >
                  <rect
                    x="1"
                    y="1"
                    width="6"
                    height="6"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="9"
                    y="1"
                    width="6"
                    height="6"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="1"
                    y="9"
                    width="6"
                    height="6"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="9"
                    y="9"
                    width="6"
                    height="6"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "list"
                    ? "bg-[#2283FF] text-white"
                    : "text-[#1E1E1E] hover:bg-gray-100"
                }`}
                title="List View"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="inline-block"
                >
                  <rect
                    x="1"
                    y="2"
                    width="14"
                    height="2"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="1"
                    y="7"
                    width="14"
                    height="2"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="1"
                    y="12"
                    width="14"
                    height="2"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>

          {isQuickEdit && !isMobile && (
            <button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className={`px-4 border border-[#E6E6E6] py-2 bg-white text-[#1E1E1E] rounded-lg ${
                isSaving ? "bg-gray-200 text-gray-500 cursor-not-allowed" : ""
              }`}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </button>
          )}
        </div>

        {/* Roster View (Card Grid) */}
        {viewMode === "roster" && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredPlayers.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                {selectedPosition === "all"
                  ? "No players found in your squad"
                  : `No ${selectedPosition} players found`}
              </div>
            ) : (
              sortedPlayers.map((player) => (
                <PlayerCard
                  key={player.id}
                  player={player}
                  isQuickEdit={isQuickEdit}
                  canEditSquad={canEditSquad}
                  onAddToSale={handleAddToSale}
                  onAddToLoan={handleAddToLoan}
                  onRemoveFromSale={handleRemoveFromSale}
                  onRemoveFromLoan={handleRemoveFromLoan}
                  onStatusChange={handleStatusChange}
                  saleAmount={playersForSale[player.id]}
                  loanAmount={playersForLoan[player.id]}
                  onDetailedPositionsSave={(payload) =>
                    handleDetailedPositionsSave(player, payload)
                  }
                  onContractExpirySave={(playerData, payload) =>
                    handleContractExpirySave(playerData, payload)
                  }
                  onPhotoUpload={handlePlayerPhotoUpload}
                  onPlayerRemoved={handlePlayerRemoved}
                />
              ))
            )}
          </div>
        )}

        {/* List View (Table) */}
        {viewMode === "list" && (
          <div className="bg-white rounded-lg border border-[#E5E6E8] overflow-hidden">
            {filteredPlayers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {selectedPosition === "all"
                  ? "No players found in your squad"
                  : `No ${selectedPosition} players found`}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F8F9FB] border-b border-[#E5E6E8]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#1E1E1E] uppercase tracking-wider">
                        Player
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#1E1E1E] uppercase tracking-wider">
                        <span
                          title="Primary / Secondary / Tertiary"
                          className="cursor-help"
                        >
                          Position (P/S/T)
                        </span>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#1E1E1E] uppercase tracking-wider">
                        Age
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#1E1E1E] uppercase tracking-wider">
                        Nationality
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#1E1E1E] uppercase tracking-wider">
                        Games
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#1E1E1E] uppercase tracking-wider">
                        Contract
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#1E1E1E] uppercase tracking-wider">
                        Status
                      </th>
                      {canEditSquad && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#1E1E1E] uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E5E6E8]">
                    {sortedPlayers.map((player) => {
                      const playerName = player.name || "-";
                      const playerInitial =
                        playerName !== "-"
                          ? playerName.charAt(0).toUpperCase()
                          : "P";

                      return (
                        <tr
                          key={player.id}
                          className="hover:bg-[#F8F9FB] transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div
                              onClick={() => {
                                const navigateId =
                                  player?.wyscoutPlayerId || player.id || null;
                                if (navigateId) {
                                  navigate(`/search/player/${navigateId}`);
                                }
                              }}
                              className="flex items-center gap-3 cursor-pointer"
                            >
                              {player.image ? (
                                <img
                                  src={player.image}
                                  alt={playerName}
                                  className="w-10 h-10 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className="w-10 h-10 rounded-full bg-gray-500 text-white flex items-center justify-center font-semibold text-sm"
                                style={{
                                  display: player.image ? "none" : "flex",
                                }}
                              >
                                {playerInitial}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-[#1E1E1E]">
                                  {playerName}
                                </div>
                                {player.jerseyNumber && (
                                  <div className="text-xs text-gray-500">
                                    #{player.jerseyNumber}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-[#1E1E1E]">
                              {(() => {
                                const detailedPositions =
                                  player.tp_detailed_position ||
                                  player.tpDetailedPosition ||
                                  {};
                                const primary =
                                  detailedPositions.primary_position ||
                                  detailedPositions.primaryPosition ||
                                  "";
                                const secondary =
                                  detailedPositions.secondary_position ||
                                  detailedPositions.secondaryPosition ||
                                  "";
                                const tertiary =
                                  detailedPositions.teritory_position ||
                                  detailedPositions.tertiary_position ||
                                  detailedPositions.tertiaryPosition ||
                                  "";

                                // If player has detailed positions, show them in P/S/T format
                                if (primary || secondary || tertiary) {
                                  const positions = [
                                    primary ? primary.toUpperCase() : "-",
                                    secondary ? secondary.toUpperCase() : "-",
                                    tertiary ? tertiary.toUpperCase() : "-",
                                  ];
                                  return positions.join(" / ");
                                }

                                // Otherwise, show normal position
                                return player.position;
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-[#1E1E1E]">
                              {player.age || "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {/* All citizenships, national team first. */}
                            <NationalityFlags
                              nationalities={player}
                              className="w-6 h-4 object-cover rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-[#1E1E1E]">
                              {player.gamesPlayed}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-[#1E1E1E]">
                              {player.contractExpiry}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {player.forSale ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                For Sale
                              </span>
                            ) : player.forLoan ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                For Loan
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Not Available
                              </span>
                            )}
                          </td>
                          {canEditSquad && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {isQuickEdit && (
                                  <>
                                    <button
                                      onClick={() =>
                                        playersForSale[player.id]
                                          ? handleRemoveFromSale(player.id)
                                          : handleAddToSale(player.id, "")
                                      }
                                      className={`px-3 py-1 text-xs rounded ${
                                        playersForSale[player.id]
                                          ? "bg-green-500 text-white"
                                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                      }`}
                                    >
                                      Sale
                                    </button>
                                    <button
                                      onClick={() =>
                                        playersForLoan[player.id]
                                          ? handleRemoveFromLoan(player.id)
                                          : handleAddToLoan(player.id, "")
                                      }
                                      className={`px-3 py-1 text-xs rounded ${
                                        playersForLoan[player.id]
                                          ? "bg-blue-500 text-white"
                                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                      }`}
                                    >
                                      Loan
                                    </button>
                                  </>
                                )}
                                <DetailedPositionsControl
                                  player={player}
                                  onSave={(payload) =>
                                    handleDetailedPositionsSave(player, payload)
                                  }
                                  onContractExpirySave={(playerData, payload) =>
                                    handleContractExpirySave(playerData, payload)
                                  }
                                  onPhotoUpload={handlePlayerPhotoUpload}
                                  onRemoveFromSquad={handleRemoveFromSquad}
                                  isFromClub={true}
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

export default MyPlayers;
